<script setup lang="ts">
import { computed } from 'vue';
import { BFormGroup, BTooltip } from 'bootstrap-vue-next';
import {
  useItpFormValidation,
  useItpFormControl,
  useItpFormContext,
  provideGroupRequired,
} from './useItpForm';

const props = defineProps<{
  id?: string;
  name?: string;
  label?: string;
  labelFor?: string;

  labelClass?: string | object;
  labelAlign?: 'left' | 'right';
  labelCols?: number | string;
  labelColsMd?: number | string;
  contentCols?: number | string;
  contentColsMd?: number | string;
  contentClass?: string | object;
  colsMd?: number | string;
  row?: boolean;
  inline?: boolean;

  description?: string;
  helpTooltip?: string;

  prependText?: string;
  appendText?: string;

  listGroupItem?: boolean;
  formCheck?: boolean;
  plainContent?: boolean;

  disabled?: boolean;
  required?: boolean;
}>();

const {
  isRequired: contextRequired,
  errors,
  state,
} = useItpFormValidation(props.name ?? '');

const formCtx = useItpFormContext();
const effectiveLabelAlign = computed<'left' | 'right' | undefined>(
  () => props.labelAlign ?? formCtx?.labelAlign?.value,
);

const groupRequiredForChildren = computed(() => !!props.required);
provideGroupRequired(groupRequiredForChildren);

const isRequired = computed(() => !!props.required || contextRequired.value);
const { isDisabled } = useItpFormControl({ disabled: props.disabled });

const slots = defineSlots<{
  default?: any;
  label?: any;
  description?: any;
  prepend?: any;
  append?: any;
}>();

const hasLabel = computed(() => !!props.label || !!slots.label);
const labelForComputed = computed(() => props.labelFor || props.name);
const invalidFeedback = computed(() => errors.value[0] || null);

const labelClassComputed = computed(() => {
  const classes: Record<string, boolean> = {
    'mx-2': !!props.inline,
    'itp-label-align-right': effectiveLabelAlign.value === 'right',
    'fw-bold': true,
  };
  if (typeof props.labelClass === 'string') {
    classes[props.labelClass] = true;
  } else if (props.labelClass) {
    Object.assign(classes, props.labelClass as object);
  }
  return classes;
});

const groupClass = computed(() => {
  const out: Record<string, boolean> = {
    'form-check': !!props.formCheck,
    'list-group-item': !!props.listGroupItem,
    'mb-3': !props.listGroupItem && !props.formCheck,
  };
  if (props.row || props.listGroupItem) {
    out.row = true;
  }
  if (props.colsMd != null) {
    out[`col-md-${props.colsMd}`] = true;
  }
  return out;
});

const contentClassComputed = computed(() => {
  const out: Record<string, boolean> = {};
  if (typeof props.contentClass === 'string') {
    out[props.contentClass] = true;
  } else if (props.contentClass) {
    Object.assign(out, props.contentClass as object);
  }
  if (props.plainContent) {
    out['form-control-plaintext'] = true;
  }
  return out;
});

const contentColsMdComputed = computed(() => props.contentColsMd);

const hasInputGroupPrepend = computed(
  () => !!props.prependText || !!slots.prepend,
);
const hasInputGroupAppend = computed(
  () => !!props.appendText || !!slots.append,
);
const hasInputGroup = computed(
  () => hasInputGroupPrepend.value || hasInputGroupAppend.value,
);

const helpId = computed(() =>
  props.id || props.name ? `${props.id || props.name}-help` : undefined,
);
</script>

<template>
  <BFormGroup
    :id="id"
    :class="groupClass"
    :description="description"
    :state="state"
    :disabled="isDisabled"
    :label-class="labelClassComputed"
    :label-for="labelForComputed"
    :label-cols="labelCols"
    :label-cols-md="labelColsMd"
    :content-cols="contentCols"
    :content-cols-md="contentColsMdComputed"
  >
    <template v-if="hasLabel" #label>
      <slot name="label">{{ label }}</slot>
      <span v-if="isRequired" class="fw-semibold ms-1">*</span>
      <sup v-if="helpTooltip && helpId">
        <span
          :id="helpId"
          class="ms-1 text-muted itp-help-icon"
          tabindex="-1"
          role="img"
          :aria-label="helpTooltip"
        >
          <iconify-icon noobserver icon="fa6-solid:circle-question" />
        </span>
      </sup>

      <BTooltip
        v-if="helpTooltip && helpId"
        :target="helpId"
        triggers="hover focus"
        placement="right"
      >
        {{ helpTooltip }}
      </BTooltip>
    </template>

    <div
      v-if="!hasInputGroup"
      class="position-relative"
      :class="[
        contentClassComputed,
        { 'itp-inline-required': !hasLabel && isRequired },
      ]"
    >
      <slot />

      <template v-if="!hasLabel && isRequired">
        <span
          class="itp-inline-required__mark fw-semibold ms-1"
          aria-hidden="true"
          >*</span
        >
      </template>

      <div v-if="invalidFeedback" class="invalid-tooltip d-block" role="alert">
        {{ invalidFeedback }}
      </div>
    </div>

    <div
      v-else
      class="input-group"
      :class="[
        contentClassComputed,
        { 'itp-inline-required': !hasLabel && isRequired },
      ]"
    >
      <span v-if="hasInputGroupPrepend" class="input-group-text">
        <slot name="prepend">{{ prependText }}</slot>
      </span>

      <slot />

      <template v-if="!hasLabel && isRequired">
        <span
          class="itp-inline-required__mark fw-semibold ms-1"
          aria-hidden="true"
          >*</span
        >
      </template>

      <span v-if="hasInputGroupAppend" class="input-group-text">
        <slot name="append">{{ appendText }}</slot>
      </span>

      <div v-if="invalidFeedback" class="invalid-tooltip d-block" role="alert">
        {{ invalidFeedback }}
      </div>
    </div>
    <template #description>
      <slot name="description" />
    </template>
  </BFormGroup>
</template>
